import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Bike, Plus, LogOut, LogIn, Trash2, Ban, Calendar, User, Camera, Image as ImageIcon,
  Gauge, Calculator, AlertTriangle, Globe, RotateCcw, Clock, X, Pencil, History, MapPin, Banknote, ArrowLeftRight, Landmark, Send,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CheckoutWizard, CheckinWizard } from "@/pages/RentalWizard";
import { BookingWizard } from "@/pages/BookingWizard";
import { BikeManager } from "@/components/BikeManager";
import { toast } from "sonner";

const eur = (v) => `€ ${Number(v || 0).toFixed(2)}`;
const STATO = {
  richiesta: "bg-[#FF9500]/15 text-[#FF9500]",
  confermata: "bg-[#007AFF]/15 text-[#4da3ff]",
  in_corso: "bg-[#FF9500]/15 text-[#FF9500]",
  concluso: "bg-[#39FF14]/15 text-[#39FF14]",
  annullata: "bg-zinc-700/40 text-zinc-400",
  in_attesa_caparra: "bg-[#f98125]/15 text-[#f98125] border border-dashed border-[#f98125]/60",
  scaduta: "bg-zinc-700/40 text-zinc-500",
};
const STATO_LABEL = { richiesta: "Da pagare", confermata: "Confermata", in_corso: "In corso", concluso: "Concluso", annullata: "Annullata", in_attesa_caparra: "Attesa caparra", scaduta: "Scaduta" };
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

function PhotoPicker({ photoName, onFile, testidPrefix }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <label className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-sm border border-[#f98125]/50 bg-[#f98125]/10 cursor-pointer hover:bg-[#f98125]/20 text-sm text-white">
        <Camera className="h-4 w-4" /> Scatta foto
        <input type="file" accept="image/*" capture="environment" className="hidden" data-testid={`${testidPrefix}-camera`} onChange={(e) => onFile(e.target.files?.[0])} />
      </label>
      <label className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-sm border border-zinc-800 cursor-pointer hover:border-zinc-700 text-sm text-zinc-400">
        <ImageIcon className="h-4 w-4" /> Galleria
        <input type="file" accept="image/*" className="hidden" data-testid={`${testidPrefix}-gallery`} onChange={(e) => onFile(e.target.files?.[0])} />
      </label>
      {photoName && <div className="col-span-2 text-xs text-[#39FF14]">Foto: {photoName}</div>}
    </div>
  );
}

const missingItemsOf = (r) => {
  const out = [];
  for (const fase of ["checkout", "checkin"]) {
    for (const o of (r?.[fase]?.foto_omesse || [])) {
      out.push({ fase, lato: o.lato, bike_id: o.bike_id, bike_numero: o.bike_numero, motivo: o.motivo });
    }
  }
  return out;
};
const latoLabel = (l) => (l === "dx" ? "Lato destro" : l === "sx" ? "Lato sinistro" : "Documento");
const faseLabel = (f) => (f === "checkout" ? "Check-out" : "Check-in");

function MissingPhotosDialog({ rental, onClose, onUpdated }) {
  const [items, setItems] = useState(missingItemsOf(rental));
  const [busyKey, setBusyKey] = useState(null);
  const keyOf = (it) => `${it.fase}-${it.lato}-${it.bike_id || "doc"}`;
  const handle = async (it, file) => {
    if (!file) return;
    const k = keyOf(it);
    setBusyKey(k);
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post(`/rentals/${rental.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const res = await api.post(`/rentals/${rental.id}/foto-mancante`, {
        fase: it.fase, lato: it.lato, bike_id: it.bike_id, photo_path: up.data.photo_path,
      });
      toast.success(`Foto ${faseLabel(it.fase)} · ${latoLabel(it.lato)} caricata`);
      const remaining = missingItemsOf(res.data);
      setItems(remaining);
      onUpdated(res.data);
      if (!remaining.length) onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Caricamento non riuscito"); }
    finally { setBusyKey(null); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#141414] border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="missing-photos-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2"><Camera className="h-5 w-5 text-[#f98125]" /> Aggiungi foto mancanti</DialogTitle>
          <DialogDescription>Foto saltate durante check-out/check-in (connessione scarsa). Caricale ora per completare la documentazione.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {items.length === 0 ? (
            <div className="text-sm text-[#39FF14] py-8 text-center" data-testid="missing-photos-empty">Tutte le foto sono state caricate.</div>
          ) : items.map((it) => (
            <div key={keyOf(it)} className="border border-amber-500/30 rounded-sm p-3" data-testid={`missing-item-${keyOf(it)}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-zinc-200 font-medium">{faseLabel(it.fase)} · {latoLabel(it.lato)}{it.bike_numero ? ` · Bici ${it.bike_numero}` : ""}</span>
                {it.motivo && <span className="text-[11px] text-amber-400">{it.motivo}</span>}
              </div>
              {busyKey === keyOf(it)
                ? <div className="text-xs text-zinc-400">Caricamento…</div>
                : <PhotoPicker onFile={(f) => handle(it, f)} testidPrefix={`missing-${keyOf(it)}`} />}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Rentals() {
  const { hasPerm } = useAuth();
  const [rentals, setRentals] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [bikes, setBikes] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [slots, setSlots] = useState([]);
  const [sedi, setSedi] = useState([]);
  const [categories, setCategories] = useState([]);
  const [methods, setMethods] = useState(["Contanti", "Carta", "Satispay", "Altro"]);
  const [contantiEnabled, setContantiEnabled] = useState(false);
  const [filter, setFilter] = useState("attivi");
  const [prepSede, setPrepSede] = useState("");
  const [prepDate, setPrepDate] = useState("");

  const load = async () => {
    try { setRentals((await api.get("/rentals")).data); } catch (e) {}
  };
  useEffect(() => {
    load();
    (async () => {
      try { setCustomers((await api.get("/customers")).data); } catch (e) {}
      try { setBikes((await api.get("/bikes")).data); } catch (e) {}
      try { setAccessories((await api.get("/pricing/accessories")).data); } catch (e) {}
      try { setSlots((await api.get("/timeslots", { params: { solo_attive: true } })).data); } catch (e) {}
      try { setSedi((await api.get("/sedi")).data); } catch (e) {}
      try { setCategories((await api.get("/categories")).data); } catch (e) {}
      try { setMethods((await api.get("/payment-methods")).data.metodi); } catch (e) {}
      try { setContantiEnabled((await api.get("/payments/backoffice-methods")).data.contanti_enabled); } catch (e) {}
    })();
  }, []);

  const accName = (id) => accessories.find((a) => a.id === id)?.nome || id;
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1" && bikes.length > 0) {
      const bikeId = searchParams.get("bike_id") || "";
      const date = searchParams.get("date") || "";
      const fasciaId = searchParams.get("fascia_id") || "";
      const categoriaId = searchParams.get("categoria_id") || bikes.find((b) => b.id === bikeId)?.categoria_noleggio || "";
      setWizardPrefill(bikeId || date || fasciaId ? { bikeId, date, fasciaId, categoriaId } : null);
      openBookingPrefill();
      searchParams.delete("new"); searchParams.delete("bike_id"); searchParams.delete("date"); searchParams.delete("fascia_id"); searchParams.delete("categoria_id");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bikes]);

  useEffect(() => {
    const rid = searchParams.get("rental");
    if (rid) {
      openDetail(rid);
      searchParams.delete("rental");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- New booking (Data + Fascia, multi-day) ----
  const [bOpen, setBOpen] = useState(false);
  const [bForm, setBForm] = useState(null);
  const [bQuote, setBQuote] = useState(null);
  const [editRental, setEditRental] = useState(null);

  const todayIso = () => new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const addDaysIso = (iso, n) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + n); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
  const slotById = (id) => slots.find((s) => s.id === id);
  const defaultSlotId = () => (slots.find((s) => s.tipo_tariffa === "giornata") || slots[0])?.id || "";
  const fmtSlotDate = (iso) => { try { return new Date(`${iso}T00:00:00`).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short" }); } catch { return iso; } };

  const giorniFromForm = (f) => (f.fasce || []).map((fid, i) => ({ data: addDaysIso(f.start_date, i), fascia_id: fid }));
  const derivedFromForm = (f) => {
    const g = giorniFromForm(f);
    if (!g.length) return null;
    const first = slotById(g[0].fascia_id), last = slotById(g[g.length - 1].fascia_id);
    if (!first || !last) return null;
    return {
      giorni: g,
      inizio: `${g[0].data}T${first.ora_inizio}:00`,
      fine: `${g[g.length - 1].data}T${last.ora_fine}:00`,
      fasce_tipi: g.map((x) => slotById(x.fascia_id)?.tipo_tariffa),
    };
  };

  // ---- Nuova prenotazione (wizard a step multi-bici) ----
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardPrefill, setWizardPrefill] = useState(null);
  const openBooking = () => { setWizardPrefill(null); setWizardOpen(true); };
  const openBookingPrefill = () => setWizardOpen(true);
  const addDay = () => setBForm((f) => ({ ...f, fasce: [...f.fasce, defaultSlotId()] }));
  const removeDay = (i) => setBForm((f) => ({ ...f, fasce: f.fasce.filter((_, idx) => idx !== i) }));
  const setDayFascia = (i, fid) => setBForm((f) => ({ ...f, fasce: f.fasce.map((x, idx) => (idx === i ? fid : x)) }));

  // Preventivo live
  useEffect(() => {
    if (!bOpen || !bForm) { return; }
    const bike = bikes.find((b) => b.id === bForm.bike_id);
    const d = derivedFromForm(bForm);
    if (!bike?.categoria_noleggio || !d || d.fasce_tipi.some((t) => !t)) { setBQuote(null); return; }
    let cancelled = false;
    api.post("/pricing/quote", {
      categoria_id: bike.categoria_noleggio, modello: bike.modello || "",
      inizio: d.inizio, fine: d.fine, fasce_tipi: d.fasce_tipi, codice_sconto: bForm.codice_sconto || "",
    }).then((r) => { if (!cancelled) setBQuote(r.data); }).catch(() => { if (!cancelled) setBQuote(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bOpen, bForm?.bike_id, JSON.stringify(bForm?.fasce), bForm?.start_date, bForm?.codice_sconto]);

  const stimaKm = async () => {
    const bike = bikes.find((b) => b.id === bForm.bike_id);
    if (!bike?.categoria_noleggio) { toast.error("La bici non ha categoria: imposta una categoria per la stima"); return; }
    const giorni = Math.max(1, (bForm.fasce || []).length);
    try {
      const res = await api.get("/rentals/estimate-km", { params: { categoria_id: bike.categoria_noleggio, giorni } });
      setBForm((f) => ({ ...f, km_stimati: res.data.km_stimati }));
      toast.success(`Stima: ${res.data.km_stimati} km (media ${res.data.media_giornaliera} km/g)`);
    } catch (e) { toast.error("Stima non disponibile"); }
  };
  const saveBooking = async () => {
    if (!bForm.customer_id || !bForm.bike_id) { toast.error("Cliente e bici obbligatori"); return; }
    const d = derivedFromForm(bForm);
    if (!d || !bForm.fasce.length) { toast.error("Seleziona almeno un giorno con la relativa fascia"); return; }
    try {
      await api.post("/rentals", {
        customer_id: bForm.customer_id, bike_id: bForm.bike_id,
        giorni: d.giorni,
        accessori: bForm.accessori, codice_sconto: bForm.codice_sconto, km_stimati: parseInt(bForm.km_stimati) || 0, note: bForm.note,
      });
      toast.success("Prenotazione creata"); setBOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  // ---- Modifica prenotazione esistente ----
  const sedeName = (sid) => sedi.find((s) => s.id === sid)?.nome || "Nessuna";
  const openEdit = (r) => {
    let start_date, fasce;
    if (r.giorni && r.giorni.length) {
      start_date = r.giorni[0].data;
      fasce = r.giorni.map((g) => g.fascia_id);
    } else {
      start_date = (r.inizio || todayIso()).slice(0, 10);
      fasce = [defaultSlotId()];
    }
    setBForm({
      editingId: r.id, stato: r.stato, customer_id: r.customer_id, bike_id: r.bike_id,
      start_date, fasce, accessori: (r.accessori || []).map((x) => (typeof x === "string" ? { id: x, qta: 1 } : { id: x.id || x.accessorio, qta: x.qta || x.quantita || 1 })), codice_sconto: r.codice_sconto || "",
      km_stimati: r.km_stimati || 0, note: r.note || "", sede_id: r.sede_id || "", motivazione: "",
    });
    setBQuote(null); setEditRental(r); setBOpen(true);
  };
  const onBikesChanged = (updated) => {
    setEditRental(updated);
    setBForm((f) => (f ? { ...f, bike_id: updated.bike_id } : f));
    load();
  };
  const saveEdit = async () => {
    if (!bForm.customer_id) { toast.error("Cliente obbligatorio"); return; }
    const d = derivedFromForm(bForm);
    if (!d || !bForm.fasce.length) { toast.error("Seleziona almeno un giorno con la relativa fascia"); return; }
    if (bForm.stato === "concluso" && !bForm.motivazione.trim()) { toast.error("Per una prenotazione conclusa indica una motivazione"); return; }
    try {
      await api.put(`/rentals/${bForm.editingId}`, {
        customer_id: bForm.customer_id, giorni: d.giorni,
        accessori: bForm.accessori, note: bForm.note,
        sede_id: bForm.sede_id || "", motivazione: bForm.motivazione || "",
      });
      toast.success("Prenotazione aggiornata"); setBOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  // ---- Check-out / Check-in wizard ----
  const [coRental, setCoRental] = useState(null);
  const [ciRental, setCiRental] = useState(null);
  const [photoRental, setPhotoRental] = useState(null);
  const [auditRental, setAuditRental] = useState(null);
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [detailRental, setDetailRental] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resendRental, setResendRental] = useState(null);
  const openCheckout = (r) => setCoRental(r);
  const openCheckin = (r) => setCiRental(r);
  const openDetail = async (rentalId) => {
    setDetailRental({ id: rentalId }); setDetailLoading(true);
    try { setDetailRental((await api.get(`/rentals/${rentalId}`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Prenotazione non trovata"); setDetailRental(null); }
    finally { setDetailLoading(false); }
  };
  const openAudit = async (r) => {
    setAuditRental(r); setAuditRows([]); setAuditLoading(true);
    try { setAuditRows((await api.get(`/rentals/${r.id}/audit`)).data.audit || []); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore caricamento cronologia"); }
    finally { setAuditLoading(false); }
  };

  const cancel = async (r) => { try { await api.put(`/rentals/${r.id}/cancel`); toast.success("Annullata"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const del = async (r) => { try { await api.delete(`/rentals/${r.id}`); toast.success("Eliminata"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const refund = async (r) => { try { await api.post(`/rentals/${r.id}/refund`); toast.success("Acconto rimborsato"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const payCashCaparra = async (r) => { try { await api.post(`/rentals/${r.id}/caparra-contanti`); toast.success("Caparra in contanti registrata · prenotazione confermata"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const confirmBonifico = async (r) => { try { await api.post(`/rentals/${r.id}/bonifico-ricevuto`); toast.success("Bonifico confermato · prenotazione confermata"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };

  const filtered = rentals.filter((r) => filter === "tutti" ? true : filter === "attivi" ? ["confermata", "in_corso"].includes(r.stato) : filter === "da_spostare" ? r.richiede_spostamento : filter === "scadute" ? (r.stato === "annullata" && (r.annullata_motivo || "").includes("Hold scaduto")) : r.stato === filter);
  const bikesOk = bikes.filter((b) => !b.a_noleggio);

  // ---- Preparazione bici del giorno (per sede) ----
  useEffect(() => {
    if (filter === "preparazione") {
      if (!prepDate) setPrepDate(addDaysIso(todayIso(), 1));
      if (!prepSede && sedi.length) setPrepSede(sedi[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, sedi]);

  const pickupDateOf = (r) => (r.giorni?.[0]?.data) || (r.inizio ? String(r.inizio).slice(0, 10) : "");
  const biciOf = (r) => (r.bici?.length ? r.bici : [{ bike_id: r.bike_id, bike_numero: r.bike_numero, sede_attuale: r.sede_id }]);
  // Sede di preparazione della singola bici: se il noleggio ha una sede di ritiro scelta, è quella
  // (e se la bici è altrove è "da trasferire"); altrimenti è la sede attuale della bici.
  const bikeSedeInfo = (r, b) => {
    const sedeRit = r.sede_ritiro || r.sede_id || null;
    if (sedeRit) return { sede: sedeRit, transfer: !!(b.sede_attuale && b.sede_attuale !== sedeRit) };
    return { sede: b.sede_attuale || b.sede_id || null, transfer: false };
  };
  const prepList = (() => {
    if (filter !== "preparazione" || !prepSede || !prepDate) return [];
    const out = [];
    for (const r of rentals) {
      if (!["confermata", "in_corso", "in_attesa_caparra"].includes(r.stato)) continue;
      if (pickupDateOf(r) !== prepDate) continue;
      const spos = Object.fromEntries((r.spostamenti_richiesti || []).filter((s) => !s.completato).map((s) => [s.bike_id, s]));
      const bikes = biciOf(r).map((b) => {
        const info = bikeSedeInfo(r, b);
        const sp = spos[b.bike_id];
        return { numero: b.bike_numero, sede: info.sede, transfer: info.transfer, partenza: sp ? sp.sede_partenza_nome : (b.sede_attuale ? sedeName(b.sede_attuale) : "") };
      }).filter((b) => b.sede === prepSede);
      if (!bikes.length) continue;
      const fascia = slotById(r.giorni?.[0]?.fascia_id);
      out.push({
        id: r.id, customer: r.customer_nome, stato: r.stato,
        fasciaLabel: fascia ? `${fascia.nome} (${fascia.ora_inizio}–${fascia.ora_fine})` : "—",
        start: fascia?.ora_inizio || "99:99",
        accessori: (r.accessori || []).map((a) => `${a.qta && a.qta > 1 ? a.qta + "× " : ""}${a.nome || accName(a.id)}`),
        bikes,
      });
    }
    out.sort((a, b) => a.start.localeCompare(b.start));
    return out;
  })();
  const prepTotBici = prepList.reduce((n, e) => n + e.bikes.length, 0);
  const prepTotTransfer = prepList.reduce((n, e) => n + e.bikes.filter((b) => b.transfer).length, 0);

  return (
    <div className="p-5 sm:p-8 max-w-7xl mx-auto fade-in">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Ritiro e rientro bici</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">NOLEGGI</h1>
        </div>
        {hasPerm("rentals.create") && (
          <button onClick={openBooking} data-testid="add-rental-btn" className="flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold uppercase tracking-wide px-5 py-3 hover:bg-[#d96a12] transition-colors active:scale-95">
            <Plus className="h-5 w-5" /> Nuova prenotazione
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {[["attivi", "Attivi"], ["in_attesa_caparra", "Attesa caparra"], ["scadute", "Scadute"], ["da_spostare", "Solo da spostare"], ["confermata", "Confermati"], ["in_corso", "In corso"], ["concluso", "Conclusi"], ["tutti", "Tutti"], ["preparazione", "Preparazione sede"]].map(([k, lbl]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`rental-filter-${k}`}
            className={`px-3 py-2 rounded-sm text-sm border transition-colors ${filter === k ? "border-[#f98125] bg-[#f98125]/10 text-white" : "border-zinc-800 text-zinc-400 hover:border-zinc-700"}`}>{lbl}</button>
        ))}
      </div>

      {filter === "preparazione" ? (
        <div className="space-y-4" data-testid="prep-view">
          <div className="bg-[#141414] border border-zinc-800 rounded-sm p-4 flex flex-wrap items-end gap-4">
            <div className="min-w-[200px]">
              <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Sede</Label>
              <Select value={prepSede} onValueChange={setPrepSede}>
                <SelectTrigger data-testid="prep-sede-select" className="mt-1"><SelectValue placeholder="Seleziona sede" /></SelectTrigger>
                <SelectContent>
                  {sedi.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`prep-sede-opt-${s.id}`}>{s.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Giorno</Label>
              <Input type="date" value={prepDate} onChange={(e) => setPrepDate(e.target.value)} data-testid="prep-date-input" className="mt-1 w-[180px]" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setPrepDate(todayIso())} data-testid="prep-today-btn" className="px-3 py-2 rounded-sm text-xs border border-zinc-800 text-zinc-400 hover:border-zinc-700">Oggi</button>
              <button onClick={() => setPrepDate(addDaysIso(todayIso(), 1))} data-testid="prep-tomorrow-btn" className="px-3 py-2 rounded-sm text-xs border border-zinc-800 text-zinc-400 hover:border-zinc-700">Domani</button>
            </div>
            <div className="ml-auto text-right">
              <div className="font-display text-2xl text-[#f98125]" data-testid="prep-count-bici">{prepTotBici} bici</div>
              <div className="text-[11px] text-zinc-500">{prepList.length} noleggi · {prepTotTransfer} da trasferire</div>
            </div>
          </div>

          {(!prepSede || !prepDate) ? (
            <div className="text-zinc-600 text-sm py-16 text-center border border-dashed border-zinc-800 rounded-sm">Seleziona una sede e un giorno.</div>
          ) : prepList.length === 0 ? (
            <div className="text-zinc-600 text-sm py-16 text-center border border-dashed border-zinc-800 rounded-sm" data-testid="prep-empty">Nessuna bici da preparare in {sedeName(prepSede)} per {fmtSlotDate(prepDate)}.</div>
          ) : (
            <div className="space-y-2">
              {prepList.map((e) => (
                <div key={e.id} data-testid={`prep-row-${e.id}`} className="bg-[#141414] border border-zinc-800 rounded-sm p-4">
                  <div className="flex items-start gap-4 flex-wrap">
                    <div className="min-w-[110px]">
                      <div className="flex items-center gap-1.5 text-[#f98125] font-semibold"><Clock className="h-4 w-4" /> {e.fasciaLabel.split(" (")[0]}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">{e.fasciaLabel.includes("(") ? e.fasciaLabel.match(/\(([^)]+)\)/)?.[1] : ""}</div>
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <div className="font-semibold flex items-center gap-2"><User className="h-3.5 w-3.5 text-zinc-500" /> {e.customer}
                        {e.stato === "in_attesa_caparra" && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-amber-500/20 text-amber-400">Attesa caparra</span>}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {e.bikes.map((b, i) => (
                          <span key={i} data-testid={`prep-bike-${e.id}-${b.numero}`}
                            className={`text-xs px-2 py-1 rounded-sm flex items-center gap-1 ${b.transfer ? "bg-amber-500/20 text-amber-300 border border-amber-500/40" : "bg-[#f98125]/15 text-[#f98125]"}`}>
                            <Bike className="h-3 w-3" /> {b.numero}
                            {b.transfer && <span className="flex items-center gap-1 text-amber-300/90"><ArrowLeftRight className="h-3 w-3" /> da {b.partenza}</span>}
                          </span>
                        ))}
                      </div>
                      {e.accessori.length > 0 && (
                        <div className="text-[11px] text-zinc-400 mt-2 flex items-start gap-1.5"><MapPin className="h-3 w-3 mt-0.5 text-zinc-600" /> Accessori: {e.accessori.join(", ")}</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-zinc-600 text-sm py-16 text-center border border-dashed border-zinc-800 rounded-sm">Nessuna prenotazione.</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => (
            <div key={r.id} data-testid={`rental-row-${r.id}`} className="bg-[#141414] border border-zinc-800 rounded-sm p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 bg-[#f98125]/15 rounded-sm flex items-center justify-center">
                    {(r.bici?.length || 1) > 1
                      ? <span className="font-display text-lg text-[#f98125] flex items-center gap-0.5"><Bike className="h-4 w-4" />{r.bici.length}</span>
                      : <span className="font-display text-xl text-[#f98125]">{r.bike_numero}</span>}
                  </div>
                  <div>
                    <div className="font-semibold flex items-center gap-2 flex-wrap"><User className="h-3.5 w-3.5 text-zinc-500" /> {r.customer_nome}
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${STATO[r.stato] || "bg-zinc-700/40 text-zinc-400"}`}>{STATO_LABEL[r.stato] || r.stato}</span>
                      {r.canale === "online" && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-[#f98125]/15 text-[#f98125] flex items-center gap-1"><Globe className="h-3 w-3" /> Online</span>}
                      {r.pagamento?.stato === "acconto_pagato" && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-400">Acconto pagato</span>}
                      {r.pagamento?.stato === "rimborsato" && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-zinc-700/40 text-zinc-400">Rimborsato</span>}
                      {r.richiede_spostamento && <span data-testid={`da-spostare-${r.id}`} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-amber-500/20 text-amber-400 flex items-center gap-1"><ArrowLeftRight className="h-3 w-3" /> Da spostare</span>}
                      {r.foto_mancanti && <span data-testid={`foto-mancanti-${r.id}`} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-[#FF3B30]/20 text-[#FF6B60] flex items-center gap-1"><Camera className="h-3 w-3" /> Foto mancanti</span>}
                    </div>
                    {r.richiede_spostamento && (r.spostamenti_richiesti || []).filter((s) => !s.completato).map((s) => (
                      <div key={s.bike_id} className="text-[11px] text-amber-400/90 mt-0.5 flex items-center gap-1"><ArrowLeftRight className="h-3 w-3" /> Bici {s.bike_numero}: da {s.sede_partenza_nome} → a {s.sede_destinazione_nome}</div>
                    ))}
                    <div className="text-xs text-zinc-500 flex items-center gap-3 mt-0.5 flex-wrap">
                      {(r.bici?.length || 1) > 1 && <span className="flex items-center gap-1 text-zinc-400" data-testid={`rental-bikes-${r.id}`}><Bike className="h-3 w-3" /> {r.bici.map((b) => b.bike_numero).join(", ")}</span>}
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(r.inizio)} → {fmtDate(r.fine)}</span>
                      <span className="flex items-center gap-1"><Gauge className="h-3 w-3" /> stima {r.km_stimati || 0} km</span>
                      {r.checkin?.km_percorsi != null && <span className="text-[#39FF14]">reali {r.checkin.km_percorsi} km</span>}
                      {r.acconto_importo > 0 && <span className="text-[#f98125]">acconto {eur(r.acconto_importo)}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {r.preventivo && <span className="font-display text-2xl text-[#f98125]">{eur(r.preventivo.totale)}</span>}
                  <button onClick={() => openAudit(r)} data-testid={`audit-btn-${r.id}`} className="p-2 text-zinc-500 hover:text-[#4da3ff] hover:bg-zinc-800 rounded-sm" title="Cronologia / audit"><History className="h-4 w-4" /></button>
                  {hasPerm("rentals.edit") && r.foto_mancanti && (
                    <button onClick={() => setPhotoRental(r)} data-testid={`add-photos-${r.id}`} className="flex items-center gap-1.5 rounded-sm border border-[#FF3B30]/50 text-[#FF6B60] px-3 py-2 text-sm hover:bg-[#FF3B30]/10" title="Aggiungi foto mancanti"><Camera className="h-4 w-4" /> Aggiungi foto ora</button>
                  )}
                  {hasPerm("rentals.edit") && r.stato === "annullata" && (r.annullata_motivo || "").includes("Hold scaduto") && (
                    <button onClick={() => setResendRental(r)} data-testid={`resend-link-${r.id}`} className="flex items-center gap-1.5 rounded-sm border border-[#f98125]/50 text-[#f98125] px-3 py-2 text-sm hover:bg-[#f98125]/10" title="Reinvia link di pagamento"><Send className="h-4 w-4" /> Reinvia link</button>
                  )}
                  {hasPerm("rentals.modify") && r.stato !== "annullata" && (
                    <button onClick={() => openEdit(r)} data-testid={`edit-rental-${r.id}`} className="p-2 text-zinc-500 hover:text-[#f98125] hover:bg-zinc-800 rounded-sm" title="Modifica prenotazione"><Pencil className="h-4 w-4" /></button>
                  )}
                  {hasPerm("rentals.edit") && r.stato === "in_attesa_caparra" && contantiEnabled && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><button data-testid={`cash-caparra-${r.id}`} className="flex items-center gap-1.5 rounded-sm bg-[#39FF14] text-[#0A0A0A] font-bold px-3 py-2 text-sm hover:bg-[#2dd60f]" title="Registra caparra in contanti"><Banknote className="h-4 w-4" /> Caparra contanti</button></AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#141414] border-zinc-800"><AlertDialogHeader><AlertDialogTitle>Registrare la caparra di {eur(r.acconto_importo)} in contanti?</AlertDialogTitle><AlertDialogDescription>L'incasso verrà registrato (metodo: contanti) e la prenotazione passerà a "Confermata", bloccando definitivamente le bici.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel><AlertDialogAction onClick={() => payCashCaparra(r)} data-testid={`cash-caparra-confirm-${r.id}`} className="rounded-sm bg-[#39FF14] text-[#0A0A0A] hover:bg-[#2dd60f]">Conferma</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                    </AlertDialog>
                  )}
                  {hasPerm("rentals.edit") && r.stato === "in_attesa_caparra" && (r.pagamento?.metodo === "Bonifico" || r.pagamento?.stato === "in_attesa_bonifico") && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><button data-testid={`bonifico-received-${r.id}`} className="flex items-center gap-1.5 rounded-sm bg-[#2b6cb0] text-white font-bold px-3 py-2 text-sm hover:bg-[#245a94]" title="Segna bonifico ricevuto"><Landmark className="h-4 w-4" /> Bonifico ricevuto</button></AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#141414] border-zinc-800"><AlertDialogHeader><AlertDialogTitle>Confermare la ricezione del bonifico di {eur(r.acconto_importo)}?</AlertDialogTitle><AlertDialogDescription>Registrerà l'incasso (metodo: bonifico) e porterà la prenotazione a "Confermata", bloccando definitivamente le bici.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel><AlertDialogAction onClick={() => confirmBonifico(r)} data-testid={`bonifico-received-confirm-${r.id}`} className="rounded-sm bg-[#2b6cb0] text-white hover:bg-[#245a94]">Conferma</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                    </AlertDialog>
                  )}
                  {hasPerm("rentals.edit") && r.stato === "confermata" && (
                    <button onClick={() => openCheckout(r)} data-testid={`checkout-btn-${r.id}`} className="flex items-center gap-1.5 rounded-sm bg-[#f98125] text-white font-bold px-3 py-2 text-sm hover:bg-[#d96a12]"><LogOut className="h-4 w-4" /> Check-out</button>
                  )}
                  {hasPerm("rentals.edit") && r.stato === "in_corso" && (
                    <button onClick={() => openCheckin(r)} data-testid={`checkin-btn-${r.id}`} className="flex items-center gap-1.5 rounded-sm bg-[#39FF14] text-[#0A0A0A] font-bold px-3 py-2 text-sm hover:bg-[#2dd60f]"><LogIn className="h-4 w-4" /> Check-in</button>
                  )}
                  {hasPerm("rentals.edit") && r.stato === "confermata" && (
                    <button onClick={() => cancel(r)} data-testid={`cancel-rental-${r.id}`} className="p-2 text-zinc-500 hover:text-[#FF9500] hover:bg-zinc-800 rounded-sm" title="Annulla"><Ban className="h-4 w-4" /></button>
                  )}
                  {hasPerm("rentals.edit") && r.pagamento?.stato === "acconto_pagato" && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><button data-testid={`refund-rental-${r.id}`} className="flex items-center gap-1.5 rounded-sm border border-zinc-700 text-zinc-300 px-3 py-2 text-sm hover:bg-zinc-800" title="Rimborsa acconto"><RotateCcw className="h-4 w-4" /> Rimborsa</button></AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#141414] border-zinc-800"><AlertDialogHeader><AlertDialogTitle>Rimborsare l'acconto di {eur(r.acconto_importo)}?</AlertDialogTitle><AlertDialogDescription>Verrà eseguito un rimborso completo dell'acconto tramite Stripe (modalità test).</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel><AlertDialogAction onClick={() => refund(r)} className="rounded-sm bg-[#f98125] hover:bg-[#d96a12]">Rimborsa</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                    </AlertDialog>
                  )}
                  {hasPerm("rentals.delete") && r.stato !== "in_corso" && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><button data-testid={`delete-rental-${r.id}`} className="p-2 text-zinc-500 hover:text-[#FF3B30] hover:bg-zinc-800 rounded-sm"><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#141414] border-zinc-800"><AlertDialogHeader><AlertDialogTitle>Eliminare la prenotazione?</AlertDialogTitle><AlertDialogDescription>Azione irreversibile.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel><AlertDialogAction onClick={() => del(r)} className="rounded-sm bg-[#FF3B30] hover:bg-[#cc2f26]">Elimina</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
              {r.foto_mancanti && (
                <div className="mt-2 pt-2 border-t border-zinc-800/60 flex flex-wrap items-center gap-2 text-[11px]" data-testid={`foto-mancanti-detail-${r.id}`}>
                  <span className="text-[#FF6B60] flex items-center gap-1"><Camera className="h-3 w-3" /> Foto mancanti:</span>
                  {missingItemsOf(r).map((it, i) => (
                    <span key={i} className="bg-[#FF3B30]/15 text-[#FF6B60] px-2 py-0.5 rounded-sm">{faseLabel(it.fase)} · {latoLabel(it.lato)}{it.bike_numero ? ` · Bici ${it.bike_numero}` : ""}</span>
                  ))}
                </div>
              )}
              {(r.accessori?.length > 0 || r.checkin?.report_id || r.storico_modifiche?.length > 0) && (
                <div className="mt-2 pt-2 border-t border-zinc-800/60 flex flex-wrap gap-1.5 text-[11px] items-center">
                  {r.accessori?.map((a, i) => { const id = typeof a === "string" ? a : a.id; const q = typeof a === "string" ? 1 : (a.qta || 1); return <span key={id || i} className="bg-zinc-800/60 text-zinc-400 px-2 py-0.5 rounded-sm">{accName(id)}{q > 1 ? ` ×${q}` : ""}</span>; })}
                  {r.checkin?.report_id && <span className="bg-[#FF3B30]/15 text-[#FF3B30] px-2 py-0.5 rounded-sm flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Danno segnalato</span>}
                  {r.storico_modifiche?.length > 0 && (
                    <span data-testid={`rental-history-${r.id}`} className="bg-zinc-800/60 text-zinc-400 px-2 py-0.5 rounded-sm flex items-center gap-1"
                      title={r.storico_modifiche.map((m) => `${fmtDate(m.at)} · ${m.utente}${m.motivazione ? ` (${m.motivazione})` : ""}: ${(m.cambi || []).map((c) => c.campo).join(", ") || "nessun campo"}`).join("\n")}>
                      <History className="h-3 w-3" /> Modificata ×{r.storico_modifiche.length}
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* New booking dialog */}
      {bForm && (
        <Dialog open={bOpen} onOpenChange={setBOpen}>
          <DialogContent className="bg-[#141414] border-zinc-800 max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2"><Bike className="h-5 w-5 text-[#f98125]" /> {bForm.editingId ? "Modifica prenotazione" : "Nuova prenotazione"}</DialogTitle>
            <DialogDescription>{bForm.editingId ? "Aggiorna i dati della prenotazione: la disponibilità e il prezzo vengono ricontrollati automaticamente." : "Seleziona cliente, bici, giorni e fasce. Il prezzo viene calcolato automaticamente."}</DialogDescription></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Cliente *</Label>
                  <Select value={bForm.customer_id} onValueChange={(v) => setBForm({ ...bForm, customer_id: v })}>
                    <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="booking-customer"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-zinc-800 max-h-60">{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome} {c.cognome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {!bForm.editingId ? (
                  <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Bici (disponibili) *</Label>
                    <Select value={bForm.bike_id} onValueChange={(v) => setBForm({ ...bForm, bike_id: v })}>
                      <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="booking-bike"><SelectValue placeholder="Seleziona bici" /></SelectTrigger>
                      <SelectContent className="bg-[#141414] border-zinc-800 max-h-60">{bikesOk.map((b) => <SelectItem key={b.id} value={b.id}>Bici {b.numero} — {b.modello || "—"}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="flex items-end"><p className="text-xs text-zinc-500 pb-2">Le bici prenotate si gestiscono nella sezione qui sotto.</p></div>
                )}
              </div>
              {bForm.editingId && editRental && (
                <div className="rounded-sm border border-zinc-800/60 bg-[#0d0d0d] p-3">
                  <BikeManager rental={editRental} bikes={bikes} categories={categories} sedi={sedi} onChanged={onBikesChanged} />
                </div>
              )}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Giorni e fasce *</Label>
                  <button type="button" onClick={addDay} data-testid="booking-add-day" className="flex items-center gap-1 text-xs text-[#f98125] hover:text-[#d96a12]"><Plus className="h-3.5 w-3.5" /> Aggiungi giorno</button>
                </div>
                <div><Label className="text-[11px] text-zinc-500">Data di inizio</Label>
                  <Input type="date" data-testid="booking-start-date" value={bForm.start_date} onChange={(e) => setBForm({ ...bForm, start_date: e.target.value })} className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
                </div>
                {slots.length === 0 ? (
                  <div className="text-xs text-[#FF9500] border border-[#FF9500]/30 rounded-sm p-2">Nessuna fascia oraria configurata. Creale in Listini → Fasce orarie.</div>
                ) : (
                  <div className="space-y-2">
                    {bForm.fasce.map((fid, i) => (
                      <div key={i} className="flex items-center gap-2" data-testid={`booking-day-${i}`}>
                        <div className="w-32 shrink-0 text-xs text-zinc-400 flex items-center gap-1"><Clock className="h-3 w-3 text-zinc-600" /> {fmtSlotDate(addDaysIso(bForm.start_date, i))}</div>
                        <Select value={fid} onValueChange={(v) => setDayFascia(i, v)}>
                          <SelectTrigger className="bg-[#0A0A0A] border-zinc-800 rounded-sm h-9 flex-1" data-testid={`booking-fascia-${i}`}><SelectValue placeholder="Fascia" /></SelectTrigger>
                          <SelectContent className="bg-[#141414] border-zinc-800">
                            {slots.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome} ({s.ora_inizio}–{s.ora_fine})</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {bForm.fasce.length > 1 && (
                          <button type="button" onClick={() => removeDay(i)} data-testid={`booking-remove-day-${i}`} className="p-1.5 text-zinc-600 hover:text-[#FF3B30]"><X className="h-4 w-4" /></button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {accessories.filter((a) => a.attivo).length > 0 && (
                <div><Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Accessori</Label>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {accessories.filter((a) => a.attivo).map((a) => {
                      const line = bForm.accessori.find((x) => x.id === a.id);
                      const q = line ? line.qta : 0;
                      const setQ = (nq) => setBForm((f) => {
                        let acc = f.accessori.filter((x) => x.id !== a.id);
                        if (nq > 0) acc = [...acc, { id: a.id, qta: nq }];
                        return { ...f, accessori: acc };
                      });
                      return (
                      <div key={a.id} data-testid={`booking-acc-${a.id}`} className={`flex items-center gap-2 px-3 py-2 rounded-sm border text-sm ${q > 0 ? "border-[#f98125] bg-[#f98125]/10" : "border-zinc-800"}`}>
                        <span className="flex-1">{a.nome} <span className="text-zinc-500">{eur(a.prezzo)}</span></span>
                        <button type="button" onClick={() => setQ(q - 1)} disabled={q <= 0} className="h-7 w-7 rounded-sm border border-zinc-700 disabled:opacity-40 hover:bg-zinc-800">−</button>
                        <span className="w-6 text-center font-mono">{q}</span>
                        <button type="button" onClick={() => setQ(q + 1)} className="h-7 w-7 rounded-sm border border-zinc-700 hover:bg-zinc-800">+</button>
                      </div>);
                    })}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 items-end">
                {bForm.editingId ? (
                  <div><Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> Sede di ritiro</Label>
                    <Select value={bForm.sede_id || "__none__"} onValueChange={(v) => setBForm({ ...bForm, sede_id: v === "__none__" ? "" : v })}>
                      <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="booking-sede"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#141414] border-zinc-800">
                        <SelectItem value="__none__">Nessuna sede</SelectItem>
                        {sedi.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Codice sconto</Label><Input data-testid="booking-codice" value={bForm.codice_sconto} onChange={(e) => setBForm({ ...bForm, codice_sconto: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm font-mono" placeholder="opzionale" /></div>
                )}
                <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Km stimati (indicativi)</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input type="number" data-testid="booking-kmstimati" value={bForm.km_stimati} onChange={(e) => setBForm({ ...bForm, km_stimati: e.target.value })} className="bg-[#0A0A0A] border-zinc-800 rounded-sm" />
                    <button onClick={stimaKm} data-testid="booking-stima-btn" className="flex items-center gap-1 px-3 rounded-sm border border-zinc-700 hover:bg-zinc-800 text-sm whitespace-nowrap"><Calculator className="h-4 w-4" /> Stima</button>
                  </div>
                </div>
              </div>
              <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Note</Label><Input data-testid="booking-note" value={bForm.note} onChange={(e) => setBForm({ ...bForm, note: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
              {bForm.editingId && bForm.stato === "concluso" && (
                <div><Label className="text-xs uppercase tracking-wider text-[#FF9500]">Motivazione rettifica * (prenotazione conclusa)</Label><Input data-testid="booking-motivazione" value={bForm.motivazione} onChange={(e) => setBForm({ ...bForm, motivazione: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-[#FF9500]/40 rounded-sm" placeholder="Indica il motivo della rettifica" /></div>
              )}
              {bQuote && (
                <div data-testid="booking-quote" className="border border-[#f98125]/30 bg-[#f98125]/5 rounded-sm p-3 space-y-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Preventivo</div>
                  {(bQuote.dettaglio || []).map((d, i) => (
                    <div key={i} className="flex justify-between text-xs text-zinc-400"><span>{d.voce}{d.qta > 1 ? ` ×${d.qta}` : ""}</span><span>{eur(d.importo)}</span></div>
                  ))}
                  {bQuote.sconto_importo > 0 && <div className="flex justify-between text-xs text-[#39FF14]"><span>Sconto {bQuote.sconto?.codice ? `(${bQuote.sconto.codice})` : ""}</span><span>− {eur(bQuote.sconto_importo)}</span></div>}
                  {bQuote.sconto_errore && <div className="text-[11px] text-[#FF9500]">{bQuote.sconto_errore}</div>}
                  <div className="flex justify-between items-center pt-1.5 border-t border-zinc-800/60">
                    <span className="text-sm text-zinc-300">Totale</span>
                    <span className="font-display text-2xl text-[#f98125]">{eur(bQuote.totale)}</span>
                  </div>
                  {bQuote.cauzione > 0 && <div className="flex justify-between text-[11px] text-zinc-500"><span>Cauzione (in sede)</span><span>{eur(bQuote.cauzione)}</span></div>}
                </div>
              )}
              <p className="text-xs text-zinc-600">Il preventivo viene calcolato in automatico dal listino della categoria della bici. I km stimati sono solo orientativi.</p>
            </div>
            <DialogFooter>
              <button onClick={() => setBOpen(false)} className="rounded-sm border border-zinc-700 px-5 py-2.5 hover:bg-zinc-800 transition-colors">Annulla</button>
              {bForm.editingId
                ? <button onClick={saveEdit} data-testid="booking-save-edit" className="rounded-sm bg-[#f98125] text-white font-bold px-5 py-2.5 hover:bg-[#d96a12] transition-colors">Salva modifiche</button>
                : <button onClick={saveBooking} data-testid="booking-save" className="rounded-sm bg-[#f98125] text-white font-bold px-5 py-2.5 hover:bg-[#d96a12] transition-colors">Crea prenotazione</button>}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Nuova prenotazione (wizard) */}
      {wizardOpen && (
        <BookingWizard prefill={wizardPrefill} onClose={() => { setWizardOpen(false); setWizardPrefill(null); }} onCreated={load}
          onImmediateCheckout={(rental) => setCoRental(rental)} />
      )}

      {/* Check-out wizard */}
      {coRental && (
        <CheckoutWizard rental={coRental} methods={methods} accName={accName}
          onClose={() => setCoRental(null)}
          onDone={async () => { setCoRental(null); await load(); }} />
      )}

      {/* Check-in wizard */}
      {ciRental && (
        <CheckinWizard rental={ciRental} methods={methods} accName={accName}
          onClose={() => setCiRental(null)}
          onDone={async () => { setCiRental(null); await load(); }} />
      )}

      {/* Aggiungi foto mancanti */}
      {photoRental && (
        <MissingPhotosDialog rental={photoRental}
          onClose={() => setPhotoRental(null)}
          onUpdated={(updated) => { setPhotoRental(updated); setRentals((rs) => rs.map((x) => (x.id === updated.id ? updated : x))); }} />
      )}

      {/* Audit trail / cronologia */}
      <AuditDialog rental={auditRental} rows={auditRows} loading={auditLoading} onClose={() => setAuditRental(null)} />

      {/* Scheda / dettaglio prenotazione (apertura da link transazioni) */}
      <RentalDetailDialog rental={detailRental} loading={detailLoading} accName={accName}
        onClose={() => setDetailRental(null)}
        onOpenAudit={(r) => { setDetailRental(null); openAudit(r); }} />

      {/* Reinvio link di pagamento (prenotazione scaduta) */}
      {resendRental && (
        <ResendLinkDialog rental={resendRental} bikes={bikes} categories={categories} sedi={sedi}
          onClose={() => setResendRental(null)} onDone={async () => { setResendRental(null); await load(); }} />
      )}
    </div>
  );
}

const AUDIT_META = {
  creazione: { icon: Plus, color: "text-[#39FF14]", label: "Creazione" },
  modifica: { icon: Pencil, color: "text-[#f98125]", label: "Modifica" },
  gestione_bici: { icon: Bike, color: "text-[#f98125]", label: "Gestione bici" },
  sostituzione: { icon: ArrowLeftRight, color: "text-[#f98125]", label: "Sostituzione bici" },
  riprogrammazione: { icon: Calendar, color: "text-[#4da3ff]", label: "Riprogrammazione" },
  check_out: { icon: LogOut, color: "text-[#f98125]", label: "Check-out" },
  check_in: { icon: LogIn, color: "text-[#39FF14]", label: "Check-in" },
  adeguamento_tariffa: { icon: Calculator, color: "text-[#f98125]", label: "Adeguamento tariffa" },
  incasso: { icon: Banknote, color: "text-[#39FF14]", label: "Incasso" },
  rimborso: { icon: RotateCcw, color: "text-[#FF6B60]", label: "Rimborso" },
  cambio_stato: { icon: RotateCcw, color: "text-[#4da3ff]", label: "Cambio stato" },
  email: { icon: Globe, color: "text-zinc-400", label: "Email" },
  annullamento: { icon: Ban, color: "text-[#FF9500]", label: "Annullamento" },
};

function AuditDialog({ rental, rows, loading, onClose }) {
  return (
    <Dialog open={!!rental} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#141414] border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="audit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-[#4da3ff]" /> Cronologia prenotazione</DialogTitle>
          <DialogDescription>Registro completo delle azioni (sola lettura): chi ha fatto cosa e quando.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center text-zinc-500 text-sm" data-testid="audit-loading">Caricamento…</div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-zinc-500 text-sm" data-testid="audit-empty">Nessuna azione registrata.</div>
        ) : (
          <ol className="relative border-l border-zinc-800 ml-3 mt-2" data-testid="audit-list">
            {rows.map((a) => {
              const m = AUDIT_META[a.azione] || { icon: Clock, color: "text-zinc-400", label: a.azione };
              const Icon = m.icon;
              return (
                <li key={a.id} className="mb-5 ml-6" data-testid={`audit-entry-${a.azione}`}>
                  <span className={`absolute -left-[13px] flex h-6 w-6 items-center justify-center rounded-full bg-[#0A0A0A] border border-zinc-700 ${m.color}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-xs font-semibold uppercase tracking-wider ${m.color}`}>{m.label}</span>
                    <time className="text-[11px] text-zinc-500">{fmtDate(a.at)}</time>
                  </div>
                  <p className="text-sm text-zinc-200 mt-0.5">{a.descrizione}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-1"><User className="h-3 w-3" /> {a.utente}</p>
                </li>
              );
            })}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RentalDetailDialog({ rental, loading, accName, onClose, onOpenAudit }) {
  const r = rental && rental.customer_nome !== undefined ? rental : null;
  return (
    <Dialog open={!!rental} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#141414] border-zinc-800 max-w-xl max-h-[85vh] overflow-y-auto" data-testid="rental-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Bike className="h-5 w-5 text-[#f98125]" /> Scheda prenotazione</DialogTitle>
          <DialogDescription>Dettaglio della prenotazione collegata alla transazione.</DialogDescription>
        </DialogHeader>
        {loading || !r ? (
          <div className="py-10 text-center text-zinc-500 text-sm" data-testid="rental-detail-loading">Caricamento…</div>
        ) : (
          <div className="space-y-4 text-sm" data-testid="rental-detail-body">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-lg font-semibold text-zinc-100" data-testid="rental-detail-cliente">{r.customer_nome || "—"}</div>
                <div className="text-[11px] font-mono text-zinc-500">#{(r.id || "").replace(/^pren_/, "")}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm ${STATO[r.stato] || "bg-zinc-700 text-zinc-300"}`}>{STATO_LABEL[r.stato] || r.stato}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-3">
                <div className="text-[10px] uppercase text-zinc-500 mb-1 flex items-center gap-1"><Calendar className="h-3 w-3" /> Periodo</div>
                <div className="text-zinc-200">{fmtDate(r.inizio)}</div>
                <div className="text-zinc-500">→ {fmtDate(r.fine)}</div>
              </div>
              <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-3">
                <div className="text-[10px] uppercase text-zinc-500 mb-1 flex items-center gap-1"><Bike className="h-3 w-3" /> Bici</div>
                <div className="text-zinc-200">{(r.bici || []).map((b) => b.bike_numero).join(", ") || r.bike_numero || "—"}</div>
                {r.sede_ritiro_nome && <div className="text-[11px] text-zinc-500 flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" /> {r.sede_ritiro_nome}</div>}
              </div>
            </div>
            {(r.accessori || []).length > 0 && (
              <div><div className="text-[10px] uppercase text-zinc-500 mb-1">Accessori</div>
                <div className="text-zinc-300">{r.accessori.map((a) => `${accName(a.id || a)}${a.quantita ? ` ×${a.quantita}` : ""}`).join(", ")}</div></div>
            )}
            <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-3 space-y-1">
              <div className="flex justify-between"><span className="text-zinc-500">Totale</span><span className="text-[#f98125] font-semibold">{eur(r.preventivo?.totale)}</span></div>
              {r.caparra > 0 && <div className="flex justify-between"><span className="text-zinc-500">Caparra</span><span>{eur(r.caparra)}</span></div>}
              {r.saldo_in_sede != null && <div className="flex justify-between"><span className="text-zinc-500">Saldo in sede</span><span>{eur(r.saldo_in_sede)}</span></div>}
              {r.preventivo?.cauzione != null && <div className="flex justify-between"><span className="text-zinc-500">Cauzione</span><span>{eur(r.preventivo.cauzione)}</span></div>}
            </div>
            {(r.pagamenti || []).length > 0 && (
              <div><div className="text-[10px] uppercase text-zinc-500 mb-1 flex items-center gap-1"><Banknote className="h-3 w-3" /> Incassi</div>
                <div className="space-y-1">
                  {r.pagamenti.map((p, i) => (
                    <div key={i} className="flex justify-between text-[13px]" data-testid={`rental-detail-pay-${i}`}>
                      <span className="text-zinc-400 capitalize">{(p.tipo || "").replace(/_/g, " ")} · {p.metodo}</span>
                      <span className={Number(p.importo) < 0 ? "text-[#FF6B60]" : "text-emerald-400"}>{eur(p.importo)}</span>
                    </div>
                  ))}
                </div></div>
            )}
            <div className="flex justify-end pt-2 border-t border-zinc-800">
              <button onClick={() => onOpenAudit(r)} data-testid="rental-detail-audit" className="flex items-center gap-1.5 rounded-sm border border-zinc-700 px-3 py-2 text-sm hover:bg-zinc-800"><History className="h-4 w-4" /> Vedi cronologia</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}



function ResendLinkDialog({ rental, bikes, categories, sedi, onClose, onDone }) {
  const [r, setR] = useState(rental);
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [showSub, setShowSub] = useState(false);
  const resend = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/rentals/${r.id}/reinvia-permalink`);
      if (res.data?.ok) {
        toast.success("Link di pagamento reinviato" + (res.data.email_inviata ? " via email" : "") + " · nuovo hold 6h");
        onDone();
      } else {
        setConflict(res.data); setShowSub(false);
        toast.error("Bici non disponibili: sostituiscile per procedere");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel reinvio"); }
    finally { setBusy(false); }
  };
  const onBikesChanged = (updated) => {
    setR(updated); setConflict(null); setShowSub(false);
    toast.success("Bici aggiornate. Ora puoi reinviare il link.");
  };
  const nomi = (r.bici || []).map((b) => b.bike_numero).join(", ") || r.bike_numero || "—";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#141414] border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="resend-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-[#f98125]" /> Reinvia link di pagamento</DialogTitle>
          <DialogDescription>La prenotazione è stata annullata per mancato pagamento entro i termini. Verrà verificata la disponibilità delle bici e, se libere, reinviato il link (nuovo hold 6h, sollecito 2h).</DialogDescription>
        </DialogHeader>
        <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-3 text-sm space-y-1">
          <div className="flex justify-between"><span className="text-zinc-500">Cliente</span><span className="font-medium">{r.customer_nome || "—"}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Periodo</span><span>{fmtDate(r.inizio)} → {fmtDate(r.fine)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Bici</span><span data-testid="resend-bici">{nomi}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Caparra</span><span className="text-[#f98125]">{eur(r.caparra)}</span></div>
        </div>
        {conflict && (
          <div className="rounded-sm border border-[#FF3B30]/50 bg-[#FF3B30]/10 p-3 text-sm" data-testid="resend-conflict">
            <div className="flex items-start gap-2 text-[#FF6B60]"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /><p>{conflict.message}</p></div>
            {!showSub && (
              <button onClick={() => setShowSub(true)} data-testid="resend-open-sub" className="mt-2 flex items-center gap-1.5 rounded-sm border border-zinc-700 px-3 py-1.5 text-xs text-zinc-200 hover:bg-zinc-800"><ArrowLeftRight className="h-3.5 w-3.5" /> Sostituisci bici</button>
            )}
          </div>
        )}
        {showSub && (
          <div className="border border-zinc-800 rounded-sm p-3" data-testid="resend-bikemanager">
            <BikeManager rental={r} bikes={bikes} categories={categories} sedi={sedi} onChanged={onBikesChanged} />
          </div>
        )}
        <DialogFooter>
          <button onClick={onClose} className="rounded-sm border border-zinc-700 px-4 py-2 text-sm hover:bg-zinc-800">Chiudi</button>
          <button onClick={resend} disabled={busy} data-testid="resend-confirm" className="flex items-center gap-1.5 rounded-sm bg-[#f98125] text-white font-bold px-4 py-2 text-sm hover:bg-[#d96a12] disabled:opacity-60">
            <Send className="h-4 w-4" /> {busy ? "Verifica…" : "Reinvia link"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
